import React, { useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import Modal, { Footer } from '@/components/common/Modal';
import { SubmitError } from '@/components/common/Forms/Utils';
import GraphQlImageModel from '@/components/interfaces/GraphQlImageModel';
import showToast from '@/components/common/Toast';
import { Image, MergeImagesResponse } from './types';
import DuplicatesBanner from './DuplicatesBanner';
import DuplicateImagesTable from './DuplicateImagesTable';
import MergeSummaryBanner from './MergeSummaryBanner';
import useDuplicateImagesStore from './useDuplicateImagesStore';
import { convertToGraphQlImageModels } from './ImageMergeModal.utils';

interface ImageMergeModalProps {
  afterSubmit: (imageModels: GraphQlImageModel[]) => void;
  afterCancel: () => void;
  closeModal: () => void;
  duplicateImages: Image[];
  isOpen: boolean;
  newImage: Image;
}

const ImageMergeModal = ({
  afterSubmit,
  afterCancel,
  closeModal,
  duplicateImages,
  isOpen,
  newImage,
}: ImageMergeModalProps) => {
  const [loading, setLoading] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const primaryImageId = useDuplicateImagesStore(state => state.primaryImageId);
  const checkedImageIds = useDuplicateImagesStore(state => state.checkedImageIds);

  const mergeImages = () => {
    setLoading(true);

    const path = Routes.plato_api_merge_images_images_path();
    Axios.post<MergeImagesResponse>(path, {
      duplicate_image_ids: Array.from(checkedImageIds),
      primary_image_id: primaryImageId,
    })
      .then((response) => {
        showToast('Images merged successfully');
        afterSubmit(convertToGraphQlImageModels(response.data));
        closeModal();
      })
      .catch(() => setHasSubmitError(true))
      .finally(() => setLoading(false));
  };

  const onCancel = () => {
    afterCancel();
    closeModal();
  };

  return (
    <Modal
      nearlyFullScreen
      overlayClassName="tw-z-[10000]"
      preventScroll
      closeModal={onCancel}
      headerText="Merge Duplicate Images"
      isOpen={isOpen}
    >
      <div className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        <DuplicatesBanner numDuplicates={duplicateImages.length} />
        <DuplicateImagesTable duplicateImages={duplicateImages} newImage={newImage} />
        <MergeSummaryBanner
          checkedImageCount={checkedImageIds.size}
          duplicateImages={duplicateImages}
          newImage={newImage}
        />
        {hasSubmitError && <SubmitError error="Error merging images. Please reload the page and try again." />}
        <Footer
          primaryButtonCallback={mergeImages}
          primaryButtonDisabled={checkedImageIds.size < 2}
          primaryButtonSubmit={false}
          primaryButtonText={`Merge Images (${checkedImageIds.size})`}
          secondaryButtonCallback={onCancel}
          secondaryButtonText="Do Not Merge"
          submitting={loading}
        />
      </div>
    </Modal>
  );
};

export default ImageMergeModal;
